/* eslint-disable no-underscore-dangle */
import qs from 'qs';
import { merge } from 'lodash';
import * as Sentry from '@sentry/nextjs';
import { InterceptorManager, ResolvedFn, RejectedFn } from './interceptor/manager';
import { isReadableStream, hasBuffer, timeout } from './utils';
import {
  RequestOptions,
  RequestPromise,
  RequestResponse,
  RequestError,
  ResponseError,
  FetchResponse,
  Method,
} from './types';

function hasHeader(options: RequestOptions, header: string) {
  return !!getHeader(options, header);
}

function getHeader(options: RequestOptions, header: string): string | undefined {
  const headerStr = header.toLowerCase();
  const key = Object.keys(options?.headers || {}).find((k) => headerStr === k.toLowerCase());
  return key ? options.headers?.[key] : undefined;
}

interface PromiseArr<T> {
  resolved: ResolvedFn<T> | ((options: RequestOptions) => RequestPromise<T>);
  rejected?: RejectedFn;
}

export class Request {
  /**
   * @description 请求默认配置
   */
  defaults: RequestOptions;

  /**
   * @description 拦截器
   */
  interceptors: {
    request: InterceptorManager<RequestOptions, RequestError>;
    response: InterceptorManager<RequestResponse, ResponseError>;
  };

  constructor(defaults?: RequestOptions) {
    this.defaults = defaults || {};
    this.interceptors = {
      request: new InterceptorManager(),
      response: new InterceptorManager(),
    };
  }

  /**
   * @description 使用指定的 options 发出HTTP请求
   * @param {RequestOptions} opts
   */
  request<T = any>(opts: RequestOptions): RequestPromise<T> {
    const options = this.validateOpts(opts);

    const chain: PromiseArr<any>[] = [
      ...this.interceptors.request.interceptors,
      {
        resolved: this._request,
        rejected: undefined,
      },
      ...this.interceptors.response.interceptors,
    ];

    let promise = Promise.resolve(options);

    while (chain.length) {
      const { resolved, rejected } = chain.shift();
      promise = promise.then(resolved, rejected);
    }

    return promise as RequestPromise<T>;
  }

  private static async _defaultAdapter<T>(opts: RequestOptions): Promise<RequestResponse<T>> {
    const res = (await fetch(opts.url, opts)) as FetchResponse;
    const data = await Request.getResponseData(opts, res);
    return Request.translateResponse<T>(opts, res, data);
  }

  protected async _request<T = any>(opts: RequestOptions = {}): RequestPromise<T> {
    const abortController = new AbortController();
    const options = { signal: abortController.signal, ...opts };

    try {
      const adapter: () => Promise<RequestResponse<T>> = options.adapter
        ? options.adapter.bind(this, options, Request._defaultAdapter.bind(this))
        : Request._defaultAdapter.bind(this, options);

      const translatedResponse =
        options.timeout > 0 ? await timeout<T>(adapter, abortController, options) : await adapter();

      if (!options.validateStatus(translatedResponse.status)) {
        throw new ResponseError<T>(
          `Request failed with status code ${translatedResponse.status}`,
          options,
          translatedResponse
        );
      }
      return translatedResponse;
    } catch (e) {
      const err = e as ResponseError;
      err.config = options;
      throw err;
    }
  }

  private static async getResponseData(opts: RequestOptions, res: FetchResponse): Promise<any> {
    switch (opts.responseType) {
      case 'stream':
        return res.body;
      case 'json': {
        let data = await res.text();
        try {
          data = JSON.parse(data);
        } catch {
          // continue
        }

        // 检查数据并上报 Sentry
        Request.checkResponseData(data, opts, res);

        return data;
      }
      case 'arraybuffer':
        return res.arrayBuffer();
      case 'blob':
        return res.blob();
      default:
        return res.text();
    }
  }

  /**
   * 检查响应数据并上报 Sentry
   * @param data 响应数据
   * @param opts 请求选项
   * @param res 响应对象
   */
  private static checkResponseData(data: any, opts: RequestOptions, res?: FetchResponse): void {
    const { url = '', baseURL = '' } = opts;

    if (
      ((url.startsWith('http://') || url.startsWith('https://')) && !url.includes('maimai.cn')) ||
      !baseURL.includes('maimai.cn')
    ) {
      // 非 maimai.cn 域名，不上报 sentry
      console.warn('Non-maimai.cn domain, not reporting to Sentry:', url);
      return;
    }
    // 如果数据不是对象，直接返回
    // 仅处理 2xx 响应
    if (typeof data !== 'object' || data === null || res?.status === 204 || res?.status >= 300) {
      return;
    }

    const shouldReportToSentry =
      // 有 result 字段且值为 'error'
      data.result === 'error' ||
      // 没有 result 字段
      !data.hasOwnProperty('result') ||
      // 有 error_code 字段
      data.hasOwnProperty('error_code') ||
      // 有 error 字段
      data.hasOwnProperty('error');

    if (shouldReportToSentry) {
      Request.reportToSentry(data, opts, res);
    }
  }

  /**
   * 获取错误详情（按题干规范版）
   * @param data 响应数据
   */
  private static getErrorDetails(data: any): string {
    /* -------- 1. 先保证是合法 JSON -------- */
    let json: any;
    if (typeof data === 'string') {
      try {
        json = JSON.parse(data);
      } catch {
        return 'Invalid JSON string';
      }
    } else if (typeof data === 'object' && data !== null) {
      json = data;
    } else {
      return 'Not a JSON';
    }

    /* -------- 2. 字段探测 -------- */
    const hasOk = json.result === 'ok';
    const hasError = 'error' in json;
    const hasCode = 'error_code' in json;
    const hasMsg = 'error_msg' in json;

    /* -------- 3. 合法格式直接返回空字符串 -------- */
    if (hasOk && !hasError && !hasCode && !hasMsg) return ''; // 正确响应
    if (!hasOk && hasError && hasCode && hasMsg) return ''; // 异常响应

    /* -------- 4. 其余都算格式错误，拼详情 -------- */
    const details: string[] = [];

    if (hasOk) details.push('Unexpected result:ok');
    if (!hasOk && !hasError && !hasCode && !hasMsg) details.push('Missing error block');
    if (hasError) details.push(`Error: ${json.error}`);
    if (hasCode) details.push(`Error Code: ${json.error_code}`);
    if (hasMsg) details.push(`Error Message: ${json.error_msg}`);

    return details.join(' | ') || 'Response format invalid';
  }

  /**
   * 上报错误到 Sentry
   * @param data 响应数据
   * @param opts 请求选项
   */
  private static reportToSentry(data: any, opts: RequestOptions, res?: FetchResponse): void {
    try {
      // 构建错误信息 - 使用新的格式
      const method = opts.method?.toUpperCase() || 'UNKNOWN';
      const url = opts.url || 'unknown';
      const status = res?.status?.toString() || '200';
      const errorDetails = Request.getErrorDetails(data);
      const errorMessage = `Client Http Error：[${method}] | ${url} | ${status} | ${errorDetails}`;
      const error = new Error(errorMessage);
      // 204 无内容响应不报错
      if (['204', '401', '403', '429'].includes(status)) {
        return;
      }

      if (!errorDetails) return;
      console.info('Reported to Sentry:', errorMessage);

      Sentry.withScope((scope) => {
        // 设置标签
        scope.setTag('from', 'client_next');
        scope.setTag('http_method', method);
        scope.setTag('http_status', status);
        scope.setTag('error_type', errorDetails);

        // 设置上下文信息
        scope.setContext('request', {
          url: opts.url,
          method: opts.method,
          headers: opts.headers,
          data: opts.data,
        });

        scope.setContext('response', {
          data: data,
          error_details: errorDetails,
          timestamp: new Date().toISOString(),
        });

        // 设置额外的数据
        scope.setExtra('request_config', {
          baseURL: opts.baseURL,
          timeout: opts.timeout,
          responseType: opts.responseType,
        });

        // 上报到 Sentry
        Sentry.captureException(error);
      });
    } catch (reportError) {
      console.error('Failed to report to Sentry:', reportError);
    }
  }

  /**
   * 校验 options, 合并默认配置.
   * @param opts
   */
  private validateOpts(options: RequestOptions): RequestOptions {
    const opts = merge({}, this.defaults, options);
    if (!opts.url) {
      throw new Error('URL is required.');
    }

    const baseUrl = opts.baseURL;
    if (baseUrl) {
      opts.url = baseUrl + opts.url;
    }

    opts.paramsSerializer = opts.paramsSerializer || Request.paramsSerializer;
    if (opts.params && Object.keys(opts.params).length > 0) {
      let additionalQueryParams = opts.paramsSerializer(opts.params);
      if (additionalQueryParams.startsWith('?')) {
        additionalQueryParams = additionalQueryParams.slice(1);
      }
      const prefix = opts.url.includes('?') ? '&' : '?';
      opts.url = opts.url + prefix + additionalQueryParams;
    }

    if (typeof options.maxContentLength === 'number') {
      opts.size = options.maxContentLength;
    }

    if (typeof options.maxRedirects === 'number') {
      opts.follow = options.maxRedirects;
    }

    opts.headers = opts.headers || ({} as Headers);
    if (opts.data) {
      const isFormData = typeof FormData === 'undefined' ? false : opts?.data instanceof FormData;
      if (isReadableStream(opts.data)) {
        opts.body = opts.data;
      } else if (hasBuffer() && Buffer.isBuffer(opts.data)) {
        opts.body = opts.data;
        if (!hasHeader(opts, 'Content-Type')) {
          opts.headers['Content-Type'] = 'application/json';
        }
      } else if (typeof opts.data === 'object') {
        // 如果设置了 www-form-urlencoded content type
        // 但数据是作为对象提供的，请使用qs序列化内容
        if (!isFormData) {
          if (getHeader(opts, 'content-type') === 'application/x-www-form-urlencoded') {
            opts.body = opts.paramsSerializer(opts.data);
          } else {
            if (!hasHeader(opts, 'Content-Type')) {
              opts.headers['Content-Type'] = 'application/json';
            }
            opts.body = JSON.stringify(opts.data);
          }
        } else {
          opts.body = opts.data;
        }
      } else {
        opts.body = opts.data;
      }
    }

    opts.validateStatus = opts.validateStatus || Request.validateStatus;
    opts.responseType = opts.responseType || 'json';
    if (!opts.headers.Accept && opts.responseType === 'json') {
      opts.headers.Accept = 'application/json';
    }
    // 将 method 改为小写
    opts.method = (opts.method || 'GET').toLowerCase() as Method;

    // 设置 credentials 默认值为 same-origin
    opts.credentials = opts.credentials || 'same-origin';
    return opts;
  }

  private static validateStatus(status: number) {
    return status >= 200 && status < 300;
  }

  private static paramsSerializer(params: { [index: string]: string | number }) {
    return qs.stringify(params);
  }

  private static translateResponse<T>(opts: RequestOptions, res: FetchResponse, data?: T): RequestResponse<T> {
    // headers 需要将 map 转换为 obj
    const headers = {} as Headers;
    res.headers.forEach((value, key) => {
      headers[key] = value;
    });

    return {
      config: opts,
      data: data as T,
      headers,
      status: res.status,
      statusText: res.statusText,
      request: {
        responseURL: res.url,
      },
    };
  }

  get<T = any>(url: string, config?: RequestOptions) {
    return this._requestMethodWithoutData<T>('get', url, config);
  }

  post<T = any>(url: string, data?: any, config?: RequestOptions) {
    return this._requestMethodWithData<T>('post', url, data, config);
  }

  private _requestMethodWithoutData<T>(method: Method, url: string, options?: RequestOptions) {
    return this.request<T>(
      Object.assign(options || {}, {
        method,
        url,
      })
    );
  }

  private _requestMethodWithData<T>(method: Method, url: string, data?: any, options?: RequestOptions) {
    return this.request<T>(
      Object.assign(options || {}, {
        method,
        url,
        data,
      })
    );
  }
}

export default Request;
